// src/layouts/DashboardLayout.jsx
import React, { useEffect, useState } from "react";
import { Outlet, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import AppNavbar from "../components/AppNarbar";      // ✅ fixed import
import Sidebar from "../components/Sidebar";
import { Menu, X } from "lucide-react";
import { useBranding } from "../contexts/BrandingContext";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import useDialog from "@/hooks/useDialog";

// A logged-out visitor who clicked "Create an account & apply" on an off-site
// job had the job stashed in sessionStorage. Now that they're authenticated,
// record the apply-intent click so they enter the follow-up loop (§3). Runs
// once per dashboard mount; clears the stash whether or not the record lands.
function drainPendingApply() {
  let pending;
  try {
    pending = JSON.parse(sessionStorage.getItem("sift_pending_apply") || "null");
  } catch (_) {
    pending = null;
  }
  if (!pending) return;
  sessionStorage.removeItem("sift_pending_apply");
  if (!pending.id) return;
  fetchWithAuth("/api/external/intents/click/", { method: "POST", json: { external_job: pending.id } })
    .then(() => {
      toast.success("We saved your application: we'll follow up with the employer.");
    })
    .catch(() => {});
}

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const branding = useBranding();
  const pageBg = branding.branded ? branding.accent_color : "var(--bg)";

  useEffect(() => { drainPendingApply(); }, []);

  // The drawer covers the page with a scrim, so it is modal for the keyboard
  // too: focus stays in it until it closes. It only exists below lg, so close
  // it if the window grows past that rather than trap focus in a hidden panel.
  const drawer = useDialog({ open, onClose: () => setOpen(false) });
  useEffect(() => {
    if (!open) return undefined;
    const wide = window.matchMedia("(min-width: 1024px)");
    const onChange = () => wide.matches && setOpen(false);
    wide.addEventListener("change", onChange);
    return () => wide.removeEventListener("change", onChange);
  }, [open]);

  return (
    <div className="min-h-screen text-ink" style={{ backgroundColor: pageBg }}>
      {/* Global top nav */}
      <AppNavbar />

      {/* Mobile top bar for opening the sidebar */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-line bg-surface">
        <button
          className="p-2 rounded-lg hover:bg-sunken text-ink"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
        >
          <Menu />
        </button>
        <div className="font-semibold text-ink">Dashboard</div>
        <div className="w-10" />
      </div>

      {/* Mobile drawer. z-[60] so it sits above the sticky AppNavbar (z-50);
          otherwise the navbar covers the top of the drawer and the first
          nav item can't be scrolled into view. */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div role="presentation"
            className="absolute inset-0 bg-ink/40"
            onClick={() => setOpen(false)}
          />
          <div
            {...drawer.props}
            aria-labelledby={undefined}
            aria-label="Menu"
            className="absolute left-0 top-0 h-full w-72 border-r border-line flex flex-col pt-[env(safe-area-inset-top)]"
            style={{ backgroundColor: branding.branded ? branding.accent_color : "#FCFAF5" }}
          >
            <div className="flex items-center justify-end px-4 py-2 shrink-0">
              <button
                className="p-2 rounded-lg hover:bg-sunken text-ink"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
              >
                <X />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <Sidebar onClose={() => setOpen(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Desktop shell: fixed sidebar + fluid main (min-w-0 prevents the
          big headings/charts from overflowing and clipping under the rail). */}
      <div className="max-w-[1480px] mx-auto flex gap-8 px-4 sm:px-6 lg:px-10 py-6">
        <aside className="hidden lg:block w-[248px] shrink-0 sticky top-24 self-start">
          <Sidebar />
        </aside>
        <main className="flex-1 min-w-0 pb-[env(safe-area-inset-bottom)]">
          <Outlet />
        </main>
      </div>

      {/* Minimal footer */}
      <footer className="border-t border-line text-tertiary text-xs py-4 px-6">
        <div className="max-w-[1480px] mx-auto flex flex-wrap justify-center gap-4">
          <span>&copy; {new Date().getFullYear()} Sift Careers, LLC</span>
          <Link to="/privacy" className="hover:text-ink transition-colors">Privacy</Link>
          <Link to="/security" className="hover:text-ink transition-colors">Security</Link>
          <Link to="/terms" className="hover:text-ink transition-colors">Terms</Link>
          <Link to="/dashboard/support" className="hover:text-ink transition-colors">Support</Link>
        </div>
      </footer>
    </div>
  );
}
